import { Address, Vault, Zero } from 'nftx.js';
import Info from './Info';
import useVaultActivity from '@/application/read/vaults/useVaultActivity';
import { useMemo } from 'react';
import { useNetwork } from '@/presentation/context/network';
import useVaultAssets from '@/application/read/assets/useVaultAssets';
import useVaultSnapshots from '@/application/read/pools/useVaultSnapshots';
import useLiquidityPools from '@/application/read/pools/useLiquidityPools';
import useCollection from '@/application/read/collections/useCollection';
import useTopStakers from '@/application/read/collections/useTopStakers';
import { isAddressWeth } from '@/domain/ticks/helpers';
interface InfoContainerProps {
  vaults?: Vault[];
  assetAddress?: Address;
  selectedVault: Vault | undefined;
  onSelectVault: (vault: Vault | undefined) => void;
}

const InfoContainer = ({
  vaults,
  assetAddress,
  selectedVault,
  onSelectVault,
}: InfoContainerProps) => {
  const { chain, network } = useNetwork();
  const { data: meta } = useCollection(assetAddress);
  const vaultIds = useMemo(() => {
    if (selectedVault) {
      return [selectedVault.vaultId];
    }
    return vaults?.map(({ vaultId }) => vaultId);
  }, [selectedVault, vaults]);

  const { data: activity, status: activityStatus } = useVaultActivity({
    vaultIds,
  });

  const { data: assets, status: assetsStatus } = useVaultAssets({
    assetAddress,
    vaultId: selectedVault?.vaultId,
  });

  const { data: snapshots, status: snapshotsStatus } = useVaultSnapshots({
    vaultIds,
  });

  const { data: pools, status: poolsStatus } = useLiquidityPools({
    vaultIds,
    exists: true,
  });

  const { data: topStakers } = useTopStakers({
    collectionAddress: assetAddress,
    vaultId: selectedVault?.vaultId,
  });

  const { totalEth, totalVToken, tvl } = useMemo(() => {
    let relevantPools = pools ?? [];
    if (selectedVault) {
      relevantPools = relevantPools.filter(
        (pool) => pool.vaultId === selectedVault.vaultId
      );
    }

    return relevantPools.reduce(
      (acc, cur) => {
        const isWeth0 = isAddressWeth(cur.tokens[0].id, network);
        const totalEth = isWeth0
          ? cur.tokens[0].balance
          : cur.tokens[1].balance;
        const totalVToken = isWeth0
          ? cur.tokens[1].balance
          : cur.tokens[0].balance;

        return {
          tvl: acc.tvl + cur.totalValueLocked,
          totalEth,
          totalVToken,
        };
      },
      {
        totalEth: Zero,
        totalVToken: Zero,
        tvl: Zero,
      }
    );
  }, [network, pools, selectedVault]);

  return (
    <Info
      imageUrl={meta?.image}
      chain={chain}
      selectedVault={selectedVault}
      onSelectVault={onSelectVault}
      vaults={vaults}
      activity={activity?.sort((a, b) => b.date - a.date).slice(0, 5)}
      activityStatus={activityStatus}
      assets={assets}
      assetsStatus={assetsStatus}
      snapshots={snapshots}
      snapshotsStatus={snapshotsStatus}
      pools={pools}
      poolsStatus={poolsStatus}
      totalEth={totalEth}
      totalVToken={totalVToken}
      tvl={tvl}
      topStakers={topStakers || []}
    />
  );
};

export default InfoContainer;
